import CurrencyMap from 'country-currency-map';
import { Currency } from 'hydra-data-types';
import { isNonEmptyString, waitForOrTimeoutInSeconds } from 'ts-utils';
import { parseCurrency } from '@util/Currency';
import { recordError } from '@util/Errors';
import { ApiClient } from '../api/ApiClient';
import { EntryState, INITIAL_ENTRY_STATE } from '../store/entry/EntryState';

const ACCEPTED_COUNTRIES = [
  'AE',
  'AT',
  'AU',
  'BE',
  'BG',
  'BR',
  'CA',
  'CH',
  'CI',
  'CR',
  'CY',
  'CZ',
  'DE',
  'DK',
  'DO',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'GT',
  'HK',
  'HU',
  'ID',
  'IE',
  'IN',
  'IT',
  'JP',
  'LT',
  'LU',
  'LV',
  'MT',
  'MX',
  'MY',
  'NL',
  'NO',
  'NZ',
  'PE',
  'PH',
  'PL',
  'PT',
  'RO',
  'SE',
  'SG',
  'SI',
  'SK',
  'SN',
  'TH',
  'TT',
  'US',
  'UY',
] as const;

export type CountryCode = (typeof ACCEPTED_COUNTRIES)[number];

/**
 * `country-currency-map`'s static dataset is wrong/incomplete for these codes:
 * - GB: the package keys the UK entry under the non-ISO abbreviation 'UK', so
 *   `getCountryByAbbreviation('GB')` returns undefined.
 * - LT/LV/SK: the dataset predates these countries' euro adoption (2015/2014/2009)
 *   and still hardcodes 'USD'.
 */
const CURRENCY_CORRECTIONS: Partial<Record<CountryCode, Currency>> = {
  GB: Currency.GBP,
  LT: Currency.EUR,
  LV: Currency.EUR,
  SK: Currency.EUR,
};

function currencyForCountry(country: CountryCode): Currency {
  const correction = CURRENCY_CORRECTIONS[country];
  if (correction !== undefined) {
    return correction;
  }
  const countryName = CurrencyMap.getCountryByAbbreviation(country);
  const currencyAbbr =
    countryName !== undefined ? CurrencyMap.getCurrencyAbbreviation(countryName) : undefined;
  return parseCurrency(currencyAbbr ?? '');
}

/**
 * The user's country and currency, from the load balancer via getClientCountry. WA gates the US-only
 * flows (Medicare / US 65+) on the country. On a failure, a missing region or no answer within
 * `timeoutSeconds` this falls back to AU, so free-access paths stay closed rather than granting on a
 * bad lookup. The backend value can be spoofed off the LB path, so it must not be treated as an auth
 * decision on its own.
 */
async function locate(
  client: Pick<ApiClient, 'getClientCountry'>,
  timeoutSeconds: number
): Promise<Pick<EntryState, 'country' | 'currency'>> {
  const { country, currency } = INITIAL_ENTRY_STATE;
  try {
    const { countryCode } = await waitForOrTimeoutInSeconds(
      client.getClientCountry(),
      timeoutSeconds,
      { throwOnTimeout: true }
    );

    // Absent when the request bypassed the load balancer or the region is unknown.
    if (isNonEmptyString(countryCode)) {
      const located = validateCountryCode(countryCode);
      if (located !== undefined) return { country: located, currency: currencyForCountry(located) };
    } else {
      // Soft-fail to AU with no throw — record so a missing X-Client-Region is visible in Sentry.
      recordError(
        new Error(`getClientCountry succeeded without countryCode (value: ${String(countryCode)})`)
      );
    }
  } catch (error) {
    recordError(error);
  }
  return { country, currency };
}

function validateCountryCode(countryCode: string): CountryCode | undefined {
  return ACCEPTED_COUNTRIES.includes(countryCode as CountryCode)
    ? (countryCode as CountryCode)
    : undefined;
}

export const GeoLocate = {
  locate,
  currencyForCountry,
};
